/**
 * Configuração centralizada das URLs da API
 * Este arquivo centraliza todas as configurações de endpoints da API
 * Todas as configurações são obtidas exclusivamente do arquivo .env
 */

// URL base da API - obtida do .env
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Autenticação - Seed para Basic Auth obtida do .env
export const AUTH_SEED = import.meta.env.VITE_AUTH_SEED;

// Limite de convites por chamada (frontend segue o backend)
export const INVITES_MAX_PER_CALL = parseInt(
  (import.meta as any).env.VITE_MAX_INVITES_PER_CALL || '10',
  10
);

// Configurações do Pagar.me
export const PAGARME_CONFIG = {
  // URL base obtida do .env
  baseUrl: import.meta.env.VITE_PAGARME_BASE_URL,

  // Endpoints específicos
  endpoints: {
    paymentLinks: '/paymentlinks',
    orders: '/orders',
    charges: '/charges',
    customers: '/customers'
  },

  // Configuração de ambiente obtida do .env
  environment: import.meta.env.VITE_PAGARME_ENVIRONMENT as 'test' | 'production',

  // Prefixos de autenticação - REMOVIDOS POR SEGURANÇA
  // As chaves agora são gerenciadas pelo backend PHP
  authPrefixes: {
    test: '', // Gerenciado pelo backend
    production: '' // Gerenciado pelo backend
  }
};

// Função para obter URL base do Pagar.me
export const getPagarmeBaseUrl = (): string => {
  return PAGARME_CONFIG.baseUrl;
};

// Função para obter endpoint completo do Pagar.me
export const getPagarmeEndpoint = (endpoint: keyof typeof PAGARME_CONFIG.endpoints): string => {
  return `${getPagarmeBaseUrl()}${PAGARME_CONFIG.endpoints[endpoint]}`;
};

// Função para validar se a chave de API está no formato correto
export const validatePagarmeApiKey = (apiKey: string): boolean => {
  const expectedPrefix = PAGARME_CONFIG.authPrefixes[PAGARME_CONFIG.environment];
  return apiKey.startsWith(expectedPrefix);
};

// URLs específicas para diferentes serviços
// Configuração de endpoints da API
export const API_ENDPOINTS = {
  // Endpoints de autenticação
  auth: {
    login: `${API_BASE_URL}/index.php?class=AuthRestService&method=login`,
    register: `${API_BASE_URL}/index.php?class=AuthRestService&method=register`,
    logout: `${API_BASE_URL}/index.php?class=AuthRestService&method=logout`,
    resetPassword: `${API_BASE_URL}/index.php?class=AuthRestService&method=resetPassword`,
    resetPasswordToken: `${API_BASE_URL}/index.php?class=AuthRestService&method=resetPasswordToken`,
    trocarSenha: `${API_BASE_URL}/index.php?class=AuthRestService&method=trocarSenha`,
    consultarPontos: `${API_BASE_URL}/index.php?class=AuthRestService&method=consultarPontos`,
  },

  checkout: {
    ping: `${API_BASE_URL}/reserva_online_checkout_rest_service/ping`,
    // {|INICIO| 02/04/2026 às 13:15 - BANNER: ENDPOINT DEDICADO PARA CONFIG DO DESCONTO (TADMIN)}
    processingFeeDiscountConfig: `${API_BASE_URL}/reserva_online_checkout_rest_service/processingFeeDiscountConfig`,
    // {|FIM| 02/04/2026 às 13:15 - BANNER: ENDPOINT DEDICADO PARA CONFIG DO DESCONTO (TADMIN)}
    checkoutCreate: `${API_BASE_URL}/reserva_online_checkout_rest_service/checkoutCreate`,
    tokenizeCard: `${API_BASE_URL}/reserva_online_checkout_rest_service/tokenizeCard`,
    getPaymentStatus: `${API_BASE_URL}/reserva_online_checkout_rest_service/checkoutStatus`,
    // Consulta de status por reserva com retorno amigável para reuso de QR Code (PIX)
    getPaymentStatusByReservation: `${API_BASE_URL}/reserva_online_checkout_rest_service/getCheckoutStatusPorReserva`,
    // Novo endpoint para cotação de taxa e totais
    quote: `${API_BASE_URL}/reserva_online_checkout_rest_service/quote`,
    // Endpoint otimizado para listar opções de parcelamento do cartão
    // Utilizado por CheckoutService.installmentOptions
    installmentOptions: `${API_BASE_URL}/reserva_online_checkout_rest_service/installmentOptions`,
    // Endpoint para resgatar voucher aplicado (treserva_voucher_uso)
    voucherUsage: `${API_BASE_URL}/reserva_online_checkout_rest_service/voucherUsage`,
    // Endpoint para remover o uso de voucher da reserva (limpeza idempotente)
    voucherClear: `${API_BASE_URL}/reserva_online_checkout_rest_service/voucherClear`,
    // Endpoint para checar se um voucher está travado por uso em outra reserva do mesmo cliente
    voucherLocked: `${API_BASE_URL}/reserva_online_checkout_rest_service/voucherLocked`,
  },

  // Endpoints de reservas
  reservations: {
    base: `${API_BASE_URL}/reserva_online`,
    create: `${API_BASE_URL}/reserva_online/create`,
    list: `${API_BASE_URL}/reserva_online/list`,
    cancel: `${API_BASE_URL}/reserva_online/cancel`,
    
  },

  // Endpoints de clientes
  clientes: {
    base: `${API_BASE_URL}/index.php?class=ClienteRestService`,
    create: `${API_BASE_URL}/index.php?class=ClienteRestService&method=create`,
    update: `${API_BASE_URL}/index.php?class=ClienteRestService&method=update`,
    get: `${API_BASE_URL}/index.php?class=ClienteRestService&method=get`,
    list: `${API_BASE_URL}/index.php?class=ClienteRestService&method=list`,
  },

  // Endpoints de localização
  location: {
    base: `${API_BASE_URL}/index.php?class=LocationRestService`,
    countries: `${API_BASE_URL}/index.php?class=LocationRestService&method=getCountries`,
    states: `${API_BASE_URL}/index.php?class=LocationRestService&method=getStates`,
    cities: `${API_BASE_URL}/index.php?class=LocationRestService&method=getCities`,
  },

  // Endpoints de termos
  terms: {
    // Visualização e aceite de termos via ReservaRestService
    base: `${API_BASE_URL}/reserva_online/obterTermo`,
    accept: `${API_BASE_URL}/reserva_online/aceitarTermo`,
  },

  // Endpoints de participantes
  participants: {
    participant_list: `${API_BASE_URL}/reserva_online_participant_rest_service/participant_list`,
    invite_generate: `${API_BASE_URL}/reserva_online_participant_rest_service/invite_generate`,
    invite_get: `${API_BASE_URL}/reserva_online_participant_rest_service/invite_get`,
    invite_send: `${API_BASE_URL}/reserva_online_participant_rest_service/invite_send`,
    // Aceite de termo vinculado ao participante (token do convite)
    accept_terms: `${API_BASE_URL}/reserva_online_participant_rest_service/participant_accept_terms`,
    // {|INICIO| 06/05/2026 às 15:10 - PARTICIPANTE: LISTAR RESERVAS ONDE O USUÁRIO É PARTICIPANTE}
    reservations_by_participant: `${API_BASE_URL}/reserva_online_participant_rest_service/reservations_by_participant`,
    // {|FIM| 06/05/2026 às 15:10 - PARTICIPANTE: LISTAR RESERVAS ONDE O USUÁRIO É PARTICIPANTE}
    createCharge: `${API_BASE_URL}/reserva_online_participant_rest_service/participant_create_charge`,
    registrarContaAssinada: `${API_BASE_URL}/reserva_online_participant_rest_service/participant_registrar_conta_assinada`,
    update: `${API_BASE_URL}/reserva_online_participant_rest_service/participant_update`,
    delete: `${API_BASE_URL}/reserva_online_participant_rest_service/participant_delete`,
    unbind: `${API_BASE_URL}/reserva_online_participant_rest_service/participant_unbind`
  },
  // {|INICIO| 09/04/2026 às 09:40 - CHECK-IN MOBILE: ADICIONAR ENDPOINTS DO MÓDULO DE CHECK-IN}
  checkin: {
    // {|INICIO| 09/04/2026 às 11:36 - RENOMEIO DO SERVIÇO DE CHECK-IN PARA "reserva_online_checkin_rest_service"}
    // {|INICIO| 06/05/2026 às 09:10 - CHECK-IN: ENDPOINT DE CONFIG PARA GATEAR BOTÃO NO FRONT}
    config: `${API_BASE_URL}/reserva_online_checkin_rest_service/config`,
    // {|FIM| 06/05/2026 às 09:10 - CHECK-IN: ENDPOINT DE CONFIG PARA GATEAR BOTÃO NO FRONT}
    validarLocalizacao: `${API_BASE_URL}/reserva_online_checkin_rest_service/validarLocalizacao`,
    confirmar: `${API_BASE_URL}/reserva_online_checkin_rest_service/confirmar`,
    status: `${API_BASE_URL}/reserva_online_checkin_rest_service/status`,
    validarQrcode: `${API_BASE_URL}/reserva_online_checkin_rest_service/validarQrcode`,
    // {|FIM| 09/04/2026 às 11:36 - RENOMEIO DO SERVIÇO DE CHECK-IN PARA "reserva_online_checkin_rest_service"}
  },
  // {|FIM| 09/04/2026 às 09:40 - CHECK-IN MOBILE: ADICIONAR ENDPOINTS DO MÓDULO DE CHECK-IN}
};

// Função para obter a URL base
export const getApiBaseUrl = (): string => {
  return API_BASE_URL;
};

// Função para obter endpoint específico
export const getApiEndpoint = (service: keyof typeof API_ENDPOINTS, endpoint?: string): string => {
  const serviceEndpoints = API_ENDPOINTS[service];
  if (typeof serviceEndpoints === 'string') {
    return serviceEndpoints;
  }
  
  if (endpoint && serviceEndpoints[endpoint as keyof typeof serviceEndpoints]) {
    return serviceEndpoints[endpoint as keyof typeof serviceEndpoints] as string;
  }
  
  if ('base' in serviceEndpoints) {
    return serviceEndpoints.base;
  }
  return serviceEndpoints[Object.keys(serviceEndpoints)[0] as keyof typeof serviceEndpoints] as string;
};
